import { useMemo, useState } from 'react'

import {
  CustomFieldRenderProps,
  F0Select,
  f0FormField,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import {
  AtsGetJobPostingSettingsBasicInfoDocument,
  AtsJobPostingSettingsBasicInformationFragment,
  AtsJobPostingSettingsEnabledFeaturesFragment,
  AtsJobPostingSettingsLocationsFragment,
  AtsJobPostingSettingsTeamsFragment,
  AtsUpdateJobPostingBasicInfoDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'
import { FactorialF0Form } from 'modules/core/components/FactorialF0Form'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import { Input } from '@factorialco/f0-react/dist/experimental'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { countryName } from 'modules/core/lib/countryList'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { JobCatalogRoleSelector } from 'modules/job_catalog/components/JobCatalogRoleSelector'
import { JobCatalogLinkedInfoAlert } from 'modules/ats/pages/JobPostingSettings/JobCatalogAlerts/LinkedInfoAlert'
import { SectionComponentLayout } from 'modules/ats/pages/JobPostingSettings/SectionComponentLayout'

import { BasicInformationSkeleton } from './BasicInformationSkeleton'

const WORKPLACE_TYPES = ['onsite', 'hybrid', 'remote'] as const

interface JobCatalogValue {
  roleUuid: string | null
  levelUuid: string | null
}

interface Props {
  jobId: string
}

export const BasicInformation = ({ jobId }: Props) => {
  const { company } = useCurrentSession()

  const { data, loading } = useQuery(AtsGetJobPostingSettingsBasicInfoDocument, {
    variables: {
      id: jobId,
      companyId: company.id,
    },
  })

  useEffect(() => {
    track(trackingEvents.ats.jobSettingsAccessBasicInformation)
  }, [])

  const job = data?.ats.jobPosting
  const locations = data?.locations.locations ?? []
  const teams = data?.teams.teams ?? []
  const enabledFeatures = data?.enabledFeatures.featuresConnection.nodes ?? []
  const jobCatalogRolesExist = (data?.jobCatalog.rolesExist.totalCount ?? 0) > 0
  const isHiringPlanEnabled =
    data?.ats._policies.sidebarSeeRecruitmentHiringPlanSubLevelItem.can ?? false

  if (loading || !job) {
    return <BasicInformationSkeleton />
  }

  return (
    <BasicInformationForm
      job={job}
      locations={locations}
      teams={teams}
      jobCatalogSerializedTree={data?.jobCatalog.treesConnection.nodes[0]?.serializedTree ?? ''}
      enabledFeatures={enabledFeatures}
      jobCatalogRolesExist={jobCatalogRolesExist}
      isHiringPlanEnabled={isHiringPlanEnabled}
    />
  )
}

interface BasicInformationFormProps {
  job: AtsJobPostingSettingsBasicInformationFragment
  locations: AtsJobPostingSettingsLocationsFragment[]
  teams: AtsJobPostingSettingsTeamsFragment[]
  jobCatalogSerializedTree: string
  enabledFeatures: AtsJobPostingSettingsEnabledFeaturesFragment[]
  jobCatalogRolesExist: boolean
  isHiringPlanEnabled: boolean
}

interface DerivedLocationInfo {
  city: string
  state: string
  country: string
  postalCode: string
}

const buildDerivedLocation = (
  location: AtsJobPostingSettingsLocationsFragment | undefined | null
): DerivedLocationInfo => ({
  city: location?.city ?? '',
  state: location?.state ?? '',
  country: location?.country ?? '',
  postalCode: location?.postalCode ?? '',
})

const BasicInformationForm = ({
  job,
  locations,
  teams,
  jobCatalogSerializedTree,
  jobCatalogRolesExist,
  enabledFeatures,
  isHiringPlanEnabled,
}: BasicInformationFormProps) => {
  const { company } = useCurrentSession()

  const jobCatalogFeatureEnabled =
    enabledFeatures.find((feature) => feature.name === 'dev_ats_job_posting_revamp')?.enabled ??
    false

  const [updateJobPosting] = useMutation(AtsUpdateJobPostingBasicInfoDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsJobPosting,
  })

  const [derivedLocation, setDerivedLocation] = useState<DerivedLocationInfo>(() =>
    buildDerivedLocation(job.location)
  )

  useUnsafeEffect(() => {
    setDerivedLocation(buildDerivedLocation(job.location))
  }, [job.location])

  const showLegalEntitySelector = company.legalEntities.length > 1 || !job.legalEntity?.id

  const legalEntityOptions = useMemo(
    () =>
      company.legalEntities.map((entity) => ({
        label: `${entity.legalName} - ${entity.currency}`,
        value: entity.id.toString(),
      })),
    [company.legalEntities]
  )

  const teamOptions = useMemo(
    () =>
      teams.map((team) => ({
        label: team.name,
        value: team.id.toString(),
      })),
    [teams]
  )

  const locationOptions = useMemo(
    () =>
      locations.map((location) => ({
        label: location.name,
        value: location.id.toString(),
      })),
    [locations]
  )

  const workplaceTypeOptions = useMemo(
    () => [
      { label: i18n.t('ats.workplace_types.onsite'), value: 'onsite' },
      { label: i18n.t('ats.workplace_types.hybrid'), value: 'hybrid' },
      { label: i18n.t('ats.workplace_types.remote'), value: 'remote' },
    ],
    []
  )

  const initialStartDate = useMemo(() => {
    if (!job.startDate) return null
    const parsed = stringToDate(job.startDate, 'backend')
    return parsed.isValid() ? parsed.toDate() : null
  }, [job.startDate])

  const schema = useMemo(
    () =>
      z.object({
        title: f0FormField(z.string().min(1, i18n.t('common.validations.empty')), {
          label: i18n.t('ats.job_posting_settings.basic_information.job_opening_label'),
          helpText: i18n.t('ats.job_posting_settings.basic_information.job_opening_description'),
          section: 'basicInfo',
        }),
        jobCatalog: f0FormField(
          z.object({
            roleUuid: z.string().nullable(),
            levelUuid: z.string().nullable(),
          }),
          {
            fieldType: 'custom',
            label: '',
            section: 'basicInfo',
            renderIf: () => jobCatalogFeatureEnabled && jobCatalogRolesExist,
            render: (props: CustomFieldRenderProps<JobCatalogValue>) => (
              <JobCatalogRoleSelector
                selectedRoleUuid={props.value?.roleUuid ?? undefined}
                selectedRoleLevelUuid={props.value?.levelUuid ?? undefined}
                onRoleChange={(roleUuid) => {
                  props.onChange({
                    roleUuid: roleUuid ?? null,
                    levelUuid: null,
                  })
                }}
                onLevelChange={(levelUuid) => {
                  props.onChange({
                    roleUuid: props.value?.roleUuid ?? null,
                    levelUuid: levelUuid ?? null,
                  })
                }}
              />
            ),
          }
        ),
        legalEntityId: f0FormField(z.string().nullable(), {
          label: i18n.t('ats.job_posting_settings.basic_information.legal_entity_label'),
          placeholder: i18n.t('common.select_basic'),
          fieldType: 'select',
          section: 'basicInfo',
          renderIf: () => showLegalEntitySelector,
          options: legalEntityOptions,
        }),
        teamId: f0FormField(z.string().nullable(), {
          label: i18n.t('ats.job_posting_settings.basic_information.team_label'),
          placeholder: i18n.t('common.select_basic'),
          fieldType: 'select',
          section: 'basicInfo',
          row: 'team-workplace-type',
          options: teamOptions,
        }),
        workplaceType: f0FormField(z.enum(WORKPLACE_TYPES).nullable(), {
          label: i18n.t('ats.job_posting_settings.basic_information.type_of_location_label'),
          placeholder: i18n.t('common.select_basic'),
          fieldType: 'select',
          section: 'basicInfo',
          row: 'team-workplace-type',
          options: workplaceTypeOptions,
        }),
        numberOfVacancies: f0FormField(
          z.number().positive(i18n.t('ats.jobs.invalid_number_of_vacancies')).nullable(),
          {
            label: i18n.t('ats.job_posting_settings.basic_information.number_of_vacancies_label'),
            fieldType: 'number',
            section: 'basicInfo',
            row: 'vacancies-start-date',
            renderIf: () => !isHiringPlanEnabled,
          }
        ),
        startDate: f0FormField(z.date().nullable(), {
          label: i18n.t('ats.job_posting_settings.basic_information.start_date_label'),
          section: 'basicInfo',
          row: 'vacancies-start-date',
        }),
        allowReferral: f0FormField(z.boolean().default(false), {
          label: i18n.t('ats.job_details.promotion.open_positions.allow_referral_label'),
          fieldType: 'checkbox',
          section: 'basicInfo',
          row: 'checkboxes',
        }),
        allowInternalApply: f0FormField(z.boolean().default(false), {
          label: i18n.t('ats.job_details.promotion.open_positions.allow_internal_apply_label'),
          fieldType: 'checkbox',
          section: 'basicInfo',
          row: 'checkboxes',
        }),
        locationId: f0FormField(z.string().min(1, i18n.t('common.validations.empty')), {
          label: i18n.t('ats.job_posting_settings.basic_information.workplace_label'),
          section: 'location',
          fieldType: 'custom',
          alert: {
            title: i18n.t('ats.jobs_form.workplace_warning_message'),
            description: '',
            variant: 'warning',
          },
          render: (props: CustomFieldRenderProps<string>) => (
            <F0Select
              label={i18n.t('ats.job_posting_settings.basic_information.workplace_label')}
              name="locationId"
              placeholder={i18n.t('common.select_basic')}
              value={props.value || undefined}
              onChange={(value: string) => {
                props.onChange(value)
                const next = locations.find((loc) => loc.id === value)
                setDerivedLocation(buildDerivedLocation(next))
              }}
              options={locationOptions}
              showSearchBox
              size="md"
              error={props.error}
              mandatory
            />
          ),
        }),
        locationDetails: f0FormField(z.unknown().optional(), {
          label: '',
          section: 'location',
          fieldType: 'custom',
          render: () => (
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <Input
                  label={i18n.t('ats.job_posting_settings.basic_information.country_label')}
                  value={countryName(derivedLocation.country)}
                  disabled
                  size="md"
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <Input
                  label={i18n.t('ats.job_posting_settings.basic_information.state_label')}
                  value={derivedLocation.state}
                  disabled
                  size="md"
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <Input
                  label={i18n.t('ats.job_posting_settings.basic_information.city_label')}
                  value={derivedLocation.city}
                  disabled
                  size="md"
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <Input
                  label={i18n.t('ats.job_posting_settings.basic_information.zip_code_label')}
                  value={derivedLocation.postalCode}
                  disabled
                  size="md"
                />
              </Box>
            </Grid>
          ),
        }),
      }),
    [
      jobCatalogFeatureEnabled,
      jobCatalogRolesExist,
      showLegalEntitySelector,
      legalEntityOptions,
      teamOptions,
      workplaceTypeOptions,
      isHiringPlanEnabled,
      locations,
      locationOptions,
      derivedLocation,
    ]
  )

  const formDefinition = useF0FormDefinition({
    name: 'ats-job-posting-basic-information',
    schema,
    sections: {
      basicInfo: {
        title: i18n.t('ats.job_posting_settings.basic_information.basic_info_title'),
      },
      location: {
        title: i18n.t('ats.job_posting_settings.basic_information.location_title'),
      },
    },
    defaultValues: {
      title: job.title,
      jobCatalog: {
        roleUuid: job.jobCatalogNodeRolesUuid ?? null,
        levelUuid: job.jobCatalogNodeLevelsUuid ?? null,
      },
      legalEntityId: job.legalEntity?.id ?? null,
      teamId: job.team?.id ?? null,
      workplaceType: job.workplaceType ?? null,
      numberOfVacancies: job.numberOfVacancies ?? null,
      startDate: initialStartDate,
      allowReferral: job.allowReferral ?? false,
      allowInternalApply: job.allowInternalApply ?? false,
      locationId: job.location?.id ?? '',
      locationDetails: undefined,
    },
    submitConfig: {
      type: 'default',
      label: i18n.t('common.submit'),
    },
    errorTriggerMode: 'on-submit',
    onSubmit: async ({ data }) => {
      let fieldErrors: Record<string, string> = {}

      const result = await updateJobPosting({
        variables: {
          id: job.id,
          allowInternalApply: data.allowInternalApply,
          allowReferral: data.allowReferral,
          title: data.title,
          teamId: data.teamId,
          legalEntityId: data.legalEntityId,
          numberOfVacancies: data.numberOfVacancies ?? null,
          workplaceType: data.workplaceType,
          locationId: data.locationId,
          startDate: data.startDate ? i18nDateToString(data.startDate, 'backend') : null,
          jobCatalogNodeRolesUuid: data.jobCatalog.roleUuid,
          jobCatalogNodeLevelsUuid: data.jobCatalog.levelUuid,
        },
      })

      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        return { success: true }
      }

      return { success: false, errors: fieldErrors }
    },
  })

  const showLinkedInformationAlert =
    jobCatalogFeatureEnabled && !!(job.jobCatalogNodeRolesUuid && job.jobCatalogNodeLevelsUuid)

  return (
    <SectionComponentLayout
      sidebar={
        showLinkedInformationAlert && (
          <JobCatalogLinkedInfoAlert
            jobCatalogSerializedTree={jobCatalogSerializedTree}
            jobCatalogLevelUuid={job.jobCatalogNodeLevelsUuid ?? ''}
            jobCatalogRoleUuid={job.jobCatalogNodeRolesUuid ?? ''}
          />
        )
      }
    >
      <FactorialF0Form formDefinition={formDefinition} />
    </SectionComponentLayout>
  )
}
